import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faBuilding } from '@fortawesome/free-solid-svg-icons';
import Tabs from '@frontend/Components/Tabs';
import SearchBar from '@frontend/Components/Inputs/SearchBar/SearchBar';
import FilterDropdown from '@frontend/Components/FilterDropdown';
import type { SourceTabType, TabType } from '../types';
import type { OrbitFilterOptions, OrbitTabItem } from '../types';

interface OrbitFiltersSectionProps {
  sourceTabs: OrbitTabItem[];
  activeSourceTab: SourceTabType;
  onSourceTabChange: (tabId: string) => void;
  typeTabs: OrbitTabItem[];
  activeTypeTab: TabType;
  onTypeTabChange: (tabId: string) => void;
  onSearch: (term: string) => void;
  searchPlaceholder: string;
  filterOptions: OrbitFilterOptions;
  assignedLeadFilter: string;
  onAssignedLeadFilterChange: (value: string) => void;
  primaryContactFilter: string;
  onPrimaryContactFilterChange: (value: string) => void;
  organizationFilter: string;
  onOrganizationFilterChange: (value: string) => void;
  roleTitleFilter: string;
  onRoleTitleFilterChange: (value: string) => void;
  mattersStatusFilter: string;
  onMattersStatusFilterChange: (value: string) => void;
}

const OrbitFiltersSection: React.FC<OrbitFiltersSectionProps> = ({
  sourceTabs,
  activeSourceTab,
  onSourceTabChange,
  typeTabs,
  activeTypeTab,
  onTypeTabChange,
  onSearch,
  searchPlaceholder,
  filterOptions,
  assignedLeadFilter,
  onAssignedLeadFilterChange,
  primaryContactFilter,
  onPrimaryContactFilterChange,
  organizationFilter,
  onOrganizationFilterChange,
  roleTitleFilter,
  onRoleTitleFilterChange,
  mattersStatusFilter,
  onMattersStatusFilterChange,
}) => (
  <div className="sept-orbit-filters-section">
    <div className="sept-orbit-tabs-search-row">
      <div className="sept-orbit-tabs-wrapper">
        <Tabs
          tabs={sourceTabs}
          activeTabId={activeSourceTab}
          onTabChange={onSourceTabChange}
          variant="pills"
          className="sept-orbit-tabs"
        />
      </div>
      <div className="sept-orbit-search-wrapper">
        <SearchBar
          onSearch={onSearch}
          placeholder={searchPlaceholder}
          className="sept-orbit-search-bar"
        />
      </div>
    </div>

    {/* Individuals / Organizations summary cards - same format as Tasks page */}
    <div className="sept-orbit-summary-cards">
      <div
        className={`sept-orbit-summary-card ${activeTypeTab === 'individuals' ? 'sept-orbit-summary-card-active' : ''}`}
        onClick={() => onTypeTabChange('individuals')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTypeTabChange('individuals');
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={activeTypeTab === 'individuals'}
        aria-label="Show individuals"
      >
        <div className="sept-orbit-summary-card-icon sept-orbit-summary-card-icon-individuals">
          <FontAwesomeIcon icon={faUser} />
        </div>
        <div className="sept-orbit-summary-card-content">
          <div className="sept-orbit-summary-card-label">Individuals</div>
          <div className="sept-orbit-summary-card-value">
            {typeTabs.find((t) => t.id === 'individuals')?.count ?? 0}
          </div>
        </div>
      </div>
      <div
        className={`sept-orbit-summary-card ${activeTypeTab === 'organizations' ? 'sept-orbit-summary-card-active' : ''}`}
        onClick={() => onTypeTabChange('organizations')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTypeTabChange('organizations');
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={activeTypeTab === 'organizations'}
        aria-label="Show organizations"
      >
        <div className="sept-orbit-summary-card-icon sept-orbit-summary-card-icon-organizations">
          <FontAwesomeIcon icon={faBuilding} />
        </div>
        <div className="sept-orbit-summary-card-content">
          <div className="sept-orbit-summary-card-label">Organizations</div>
          <div className="sept-orbit-summary-card-value">
            {typeTabs.find((t) => t.id === 'organizations')?.count ?? 0}
          </div>
        </div>
      </div>
    </div>

    <div className="sept-orbit-filters">
      <FilterDropdown
        label=""
        placeholder="All Leads"
        options={[
          { value: 'all', label: 'All Leads' },
          ...filterOptions.assignedLeads,
        ]}
        value={assignedLeadFilter}
        onChange={(value) => onAssignedLeadFilterChange(Array.isArray(value) ? value[0] : value)}
        className="sept-orbit-filter"
      />
      <FilterDropdown
        label=""
        placeholder="All Contacts"
        options={[
          { value: 'all', label: 'All Contacts' },
          ...(filterOptions.primaryContacts || []),
        ]}
        value={primaryContactFilter}
        onChange={(value) => onPrimaryContactFilterChange(Array.isArray(value) ? value[0] : value)}
        className="sept-orbit-filter"
      />
      {activeTypeTab === 'individuals' && (
        <FilterDropdown
          label=""
          placeholder="All Organizations"
          options={[
            { value: 'all', label: 'All Organizations' },
            ...filterOptions.organizations,
          ]}
          value={organizationFilter}
          onChange={(value) => onOrganizationFilterChange(Array.isArray(value) ? value[0] : value)}
          className="sept-orbit-filter"
        />
      )}
      {activeTypeTab === 'individuals' && (
        <FilterDropdown
          label=""
          placeholder="All Roles"
          options={[
            { value: 'all', label: 'All Roles' },
            ...filterOptions.roleTitles,
          ]}
          value={roleTitleFilter}
          onChange={(value) => onRoleTitleFilterChange(Array.isArray(value) ? value[0] : value)}
          className="sept-orbit-filter"
        />
      )}
      <FilterDropdown
        label=""
        placeholder="All Statuses"
        options={[
          { value: 'all', label: 'All Statuses' },
          ...filterOptions.matterStatuses,
        ]}
        value={mattersStatusFilter}
        onChange={(value) => onMattersStatusFilterChange(Array.isArray(value) ? value[0] : value)}
        className="sept-orbit-filter"
      />
    </div>
  </div>
);

export default OrbitFiltersSection;
